//import moment from "moment";
import moment, {DurationInputArg2, Moment} from "moment";
import {API_COMMON_DATE, COMMON_DATE_MOMENT, DISPLAY_DATE_FORMAT, DISPLAY_DATETIME_MOMENT} from '@shared/constants';


export interface IRangeDate {
  fromDate: string,
  toDate: string,
}

type DateTypeConvert = 'date' | 'time' | 'displayDate' | 'displayDateTime' | 'commonDateTime'


export function calRangeDatePicker(startDate: string, endDate: string) {
  const rangeDateFirst = (startDate: Moment) => {
    return {
      fromDate: startDate.format(DISPLAY_DATE_FORMAT),
      toDate: startDate.endOf('month').format(DISPLAY_DATE_FORMAT),
    }
  }

  const rangeDateLast = (endDate: Moment) => {
    return {
      toDate: endDate.format(DISPLAY_DATE_FORMAT),
      fromDate: endDate.startOf('month').format(DISPLAY_DATE_FORMAT),
    }
  }

  const rangesArr: IRangeDate[] = [];
  if (!startDate || !endDate) {
    return rangesArr;
  }

  const eventStartTime = new Date(startDate);
  const eventEndTime = new Date(endDate);
  const mStart = moment(eventStartTime);
  const mEnd = moment(eventEndTime);
  if (mStart.month() === mEnd.month()) {
    rangesArr.push({
      fromDate: mStart.format(DISPLAY_DATE_FORMAT),
      toDate: mEnd.format(DISPLAY_DATE_FORMAT),
    });
    return rangesArr;
  }

  const months = mEnd.diff(eventStartTime, 'months');
  const ckMonth = months + 1;
  // Số tháng mặc định bắt đầu từ 0, nên luôn cần +1
  if (ckMonth == 2) {
    rangesArr.push(rangeDateFirst(mStart));
    rangesArr.push(rangeDateLast(mEnd));
    return rangesArr;
  }

  for (let i = 1; i <= ckMonth; i++) {
    if (i == 1) {
      rangesArr.push(rangeDateFirst(mStart));
    } else if (i == ckMonth) {
      rangesArr.push(rangeDateLast(mEnd));
    } else {
      const nextMonth = moment(mStart);
      nextMonth.add(i - 1, 'month');
      rangesArr.push({
        fromDate: nextMonth.startOf('month').format(DISPLAY_DATE_FORMAT),
        toDate: nextMonth.endOf('month').format(DISPLAY_DATE_FORMAT),
      });
    }
  }
  return rangesArr;
}

interface IResDateRangeUntil {
  start: Moment
  end: Moment
}

/**
 * Hàm tạo khoảng date range
 * */
export function dateRangeUntil(range: string): IResDateRangeUntil | null {
  let unit: DurationInputArg2 = 'd'
  let subtract = 0
  if (['YESTERDAY', 'LAST_WEEK', 'LAST_MONTH'].includes(range)) {
    subtract = 1
  }
  switch (range) {
    case 'THIS_WEEK':
    case 'LAST_WEEK':
      unit = 'w'
      break;

    case 'THIS_MONTH':
    case 'LAST_MONTH':
      unit = 'M'
      break;
  }

  let end = moment().subtract(subtract, unit).endOf(unit)
  if (end.diff(moment(), 'd') > 0) {
    end = moment();
  }
  const start = moment().subtract(subtract, unit).startOf(unit)
  if (!end.isValid() || !start.isValid()) {
    return null;
  }
  return {start, end}
}

/**
 * Hàm tạo khoảng date range dùng cho việc lọc theo các kỳ
 * */
export function datePeriodUntil(range: string): IResDateRangeUntil | null {
  let unit: DurationInputArg2 = 'day';
  let subtract = 0;

  if (['YESTERDAY', 'LAST_WEEK', 'LAST_MONTH'].includes(range)) {
    subtract = 1;
  }

  switch (range) {
    case 'THIS_WEEK':
    case 'LAST_WEEK':
      unit = 'w';
      break;

    case 'THIS_MONTH':
    case 'LAST_MONTH':
      unit = 'M';
      break;

    case 'SEVEN_DATE':
      subtract = 6;
      break;

    case 'FOURTEEN_DAYS':
      subtract = 13;
      break;

    case 'THIRTY_DAYS':
      subtract = 29;
      break;

    case 'SIXTY_DAYS':
      subtract = 59;
      break;

    default:
      return null;
  }
  let end: Moment;

  if (subtract > 1) {
    end = moment().endOf('day');

    if (end.diff(moment(), 'days') > 0) {
      end = moment();
    }
  } else {
    end = moment().subtract(subtract, unit).endOf(unit);

    if (end.diff(moment(), 'days') > 0) {
      end = moment();
    }
  }

  if (range == 'THIS_WEEK') {
    end = end.endOf('w');
  } else if (range == 'THIS_MONTH') {
    end = end.endOf('M');
  }

  const start = moment().subtract(subtract, unit).startOf(unit);

  if (!end.isValid() || !start.isValid()) {
    return null;
  }

  return {start, end};
}

// trả về thời gian từ tháng hiện tại tới tháng trước đó
export function monthPeriodUntil(month: number) {
  const end = moment().endOf('month');
  const start = moment().subtract(month, 'M').startOf('M');
  return {start, end};
}

/**
 * trả về: ngày hiện tại trừ đi số ngày truyền vào
 * @param day
 * @param format
 */
export function diffDay(day: number, format = API_COMMON_DATE) {
  return moment().subtract(day, 'days').format(format);
}

/**
 * trả về: ngày hiện tại trừ đi số tháng truyền vào
 * @param month
 * @param format
 */
export function diffMonth(month: number, format = API_COMMON_DATE) {
  return moment().subtract(month, 'months').format(format);
}


/**
 * format giá trị kiểu date, dateTime, time trước khi call api để gửi dữ liệu đi
 * @param values
 * @param records
 */
export function commonFormatDateUtil(values: any, records: Record<string, DateTypeConvert>) {
  const newValues = {...values};
  Object.entries(records).forEach(([k, type]) => {
    newValues[k] = formatDateType(newValues[k], type);
  })
  return newValues;
}

/**
 * format giá trị kiểu date, dateTime, time trước khi call api để gửi dữ liệu đi
 */
export function formatDateType(value: any, type: DateTypeConvert) {
  if (!value) {
    return '';
  }

  let format = DISPLAY_DATE_FORMAT; //kiểu dữ liệu mong muốn
  switch (type) {
    case 'displayDateTime':
      format = DISPLAY_DATETIME_MOMENT;
      break;
    case 'date':
      format = COMMON_DATE_MOMENT;
      break;
    case 'displayDate':
      format = DISPLAY_DATE_FORMAT;
      break;
    case 'time':
      break;
  }

  // Mảng các định dạng đầu vào có thể có(kiểu giá trị của value phải nằm trong inputFormats]
  const inputFormats = [COMMON_DATE_MOMENT, DISPLAY_DATE_FORMAT];

  // Chuyển value thành đối tượng moment với mảng các định dạng đầu vào
  const newValue = moment(value, inputFormats).format(format);
  if (newValue == "Invalid date") {
    return ''
  }
  return newValue;
}

// Hàm lấy ngày cuối cùng của tháng hiện tại
export function lastDayCurrentInMonth() {
  return moment().endOf('months').format(API_COMMON_DATE);
}

// Hàm lấy ngày đầu tiên của tháng trước
export function firstDayInLastMonth() {
  return moment().subtract(1, 'months').startOf('month').format(API_COMMON_DATE);
}

// Hàm lấy ngày cuối cùng của tháng trước
export function currentDayInLastMonth() {
  return moment().subtract(1, 'months').endOf('month').format(API_COMMON_DATE);
}

// Hàm lấy ngày đầu tiên của tháng hiện tại
export function firstDayInMonth() {
  return moment().startOf('months').format(API_COMMON_DATE);
}

// Hàm lấy ngày hiện tại
export function currentDayInMonth() {
  return moment().format(API_COMMON_DATE);
}

// Hàm lấy ngày tiếp theo
export function nextDay() {
  return moment().add(1, 'days').format(API_COMMON_DATE);
}

// Hàm lấy ngày đầu tiên của nhiều tháng trước
export function firstDayInLastNumberMonth(months: number) {
  return moment().subtract(months, 'months').startOf('month').format(API_COMMON_DATE);
}

// Hàm lấy ngày đầu tiên của nhiều tháng tới
export function firstDayInNextNumberMonth(months: number) {
  return moment().add(months, 'months').startOf('month').format(API_COMMON_DATE);
}

export function momentDiffTimeStamp(timestamp1: number, timestamp2: number, unit: DurationInputArg2) {
  return moment.unix(timestamp1).diff(moment.unix(timestamp2), unit);
}

export function timeStampUnix() {
  return moment().unix();
}

export function formatTimeStampToString(timestamp: number, format = DISPLAY_DATE_FORMAT) {
  if (timestamp.toString().length === 10) {
    timestamp *= 1000;
  }
  return moment(timestamp).format(format);
}

export function hasOldDateFormat(dateString: any) {
  if (dateString && typeof dateString == 'string') {
    // check chuỗi có khớp định dạng d/m/y hay không (ví dụ: 01/10/2024)
    const datePattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const match = dateString.match(datePattern);
    if (match) {
      const day = parseInt(match[1], 10);
      const month = parseInt(match[2], 10);
      const year = parseInt(match[3], 10);
      // check ngày hợp lệ
      const date = new Date(year, month - 1, day);
      if (
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day
      ) {
        // Trả về định dạng y-m-d
        return `${year}-${month < 10 ? '0' + month : month.toString()}-${day < 10 ? '0' + day : day.toString()}`;
      }
    }
  }
  return null;
}


export function getDiffDate(from: string, to: string | null = null): number {
  const fromDate = new Date(from);
  const toDate = to ? new Date(to) : new Date();

  if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
    return 0;
  }

  const diffYears = toDate.getFullYear() - fromDate.getFullYear();
  const diffMonths = toDate.getMonth() - fromDate.getMonth();

  return diffYears * 12 + diffMonths;
}


export type RangeDateDefaultFilter = 'Today' | 'ThisWeek' | 'ThisMonth' | '7d' | '14d' | '30d' | '60d' | '90d';

export function getDateRange(range: RangeDateDefaultFilter): { start: Moment; end: Moment } | null {
  let start: Moment;
  let end: Moment;

  switch (range) {
    case 'Today':
      start = moment().startOf('day');
      end = moment().endOf('day');
      break;

    case 'ThisWeek':
      start = moment().startOf('week');
      end = moment().endOf('week');
      break;

    case 'ThisMonth':
      start = moment().startOf('month');
      end = moment().endOf('month');
      break;

    case '7d':
      start = moment().subtract(6, 'days').startOf('day'); // Bao gồm cả ngày hôm nay
      end = moment().endOf('day');
      break;

    case '14d':
      start = moment().subtract(13, 'days').startOf('day');
      end = moment().endOf('day');
      break;

    case '30d':
      start = moment().subtract(29, 'days').startOf('day');
      end = moment().endOf('day');
      break;

    case '60d':
      start = moment().subtract(59, 'days').startOf('day');
      end = moment().endOf('day');
      break;

    case '90d':
      start = moment().subtract(89, 'days').startOf('day');
      end = moment().endOf('day');
      break;

    default:
      return null;
  }

  if (!start.isValid() || !end.isValid()) {
    return null;
  }

  return {start, end};
}
